import { useEffect, useRef, type CSSProperties } from 'react';
import { motion } from 'motion/react';

interface TypewriterTitleProps {
  /** Full text to reveal. Re-revealing restarts when this changes. */
  text: string;
  /** Stagger between successive letters' fade-ins (ms). Default 32 ms
   *  — tight enough that long card names don't drag, loose enough
   *  that the eye reads it as a wipe rather than a flash. */
  charDelayMs?: number;
  /** How long each letter takes to fade from 0 → 1 (ms). The fades
   *  overlap thanks to `charDelayMs`, so several letters are mid-
   *  fade at any instant — that's what produces the soft "ramp"
   *  edge instead of a hard typewriter cursor. Default 380 ms. */
  fadeDurationMs?: number;
  /** Pause before the first letter starts fading (ms). Lets the
   *  parent's container entrance land before the title kicks off. */
  startDelayMs?: number;
  /** Optional className applied to the outer <h2>. */
  className?: string;
  /** Optional inline style overrides. */
  style?: CSSProperties;
  /** Fires once the LAST letter finishes its fade. Lets a parent
   *  orchestrator hand off to the next reveal beat without guessing
   *  the total stagger duration. */
  onComplete?: () => void;
  /** Returning view — render every letter solid from the first paint
   *  (no per-letter wipe) and fire `onComplete` on the next tick so
   *  nothing downstream waits on a fade that never plays. */
  instant?: boolean;
}

/**
 * Title revealed letter-by-letter as a soft left→right wipe.
 * Each character is its own `motion.span` that fades from
 * `opacity: 0` to `1`, staggered by `charDelayMs`. Because every
 * letter takes `fadeDurationMs` to fade and the next one starts
 * only `charDelayMs` later, several letters are always mid-fade —
 * the trailing edge of the wipe is a gradient of opacities, not a
 * hard cut. Visually this reads like "letters materialising" in a
 * smooth ramp from clear → cloudy → solid, rather than the harsh
 * pop of a classic typewriter.
 *
 * Layout box stays stable from the first paint — every span is
 * rendered up-front in its final slot, only opacity changes.
 * Siblings (card image, FMV row, Buyback CTA) don't shuffle as
 * letters appear. Whitespace is preserved via `whiteSpace:
 * 'pre-wrap'` so multi-word titles still wrap naturally.
 */
export function TypewriterTitle({
  text,
  charDelayMs = 32,
  fadeDurationMs = 380,
  startDelayMs = 120,
  className = '',
  style,
  onComplete,
  instant = false,
}: TypewriterTitleProps) {
  // Stable ref so re-renders with a fresh callback identity don't
  // restart the completion timer.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    // Returning view: no wipe to wait on — hand off on the next tick.
    if (instant) {
      const t = window.setTimeout(() => onCompleteRef.current?.(), 0);
      return () => window.clearTimeout(t);
    }
    // The last letter starts its fade at `startDelay + (n-1)*charDelay`
    // and finishes `fadeDuration` later. Fire onComplete once that
    // tail settles.
    const total =
      startDelayMs +
      Math.max(0, text.length - 1) * charDelayMs +
      fadeDurationMs;
    const t = window.setTimeout(() => {
      onCompleteRef.current?.();
    }, total);
    return () => window.clearTimeout(t);
  }, [text, charDelayMs, fadeDurationMs, startDelayMs, instant]);

  return (
    <h2
      className={className}
      style={{ whiteSpace: 'pre-wrap', ...style }}
      aria-label={text}
    >
      {Array.from(text).map((char, i) => (
        <motion.span
          // Include the char in the key so re-rendering with a new
          // string remounts the spans and replays the fade from 0.
          key={`${i}-${char}`}
          aria-hidden
          initial={{ opacity: instant ? 1 : 0 }}
          animate={{ opacity: 1 }}
          transition={
            instant
              ? { duration: 0 }
              : {
                  duration: fadeDurationMs / 1000,
                  delay: (startDelayMs + i * charDelayMs) / 1000,
                  ease: 'easeOut',
                }
          }
        >
          {char}
        </motion.span>
      ))}
    </h2>
  );
}
